/**
 * GET /api/v1/meta — the client bootstrap call.
 *
 * The server owns the limits it enforces (max upload size, accepted MIME types,
 * page-count warning threshold, tax rate). The UI must read them from here
 * instead of hardcoding, or we end up promising an upload the server rejects.
 */
import { api } from './client';
import { queryKeys, useApiQuery } from './queryClient';
import type { MetaResponse } from './types';

export const META_PATH = '/api/v1/meta';

export function fetchMeta(signal?: AbortSignal): Promise<MetaResponse> {
  return api.get<MetaResponse>(META_PATH, signal ? { signal } : undefined);
}

/**
 * Meta rarely changes within a session, so it is cached for 5 minutes. It is not
 * `Infinity`: a deploy that changes MAX_UPLOAD_MB should reach a long-lived tab
 * eventually without a hard reload.
 */
export function useMeta() {
  return useApiQuery<MetaResponse, typeof queryKeys.meta>({
    queryKey: queryKeys.meta,
    queryFn: ({ signal }) => fetchMeta(signal),
    staleTime: 5 * 60_000,
  });
}

export const REOPEN_ALERTS_PATH = '/api/v1/reopen-alerts';

/**
 * Ask to be texted when the shop starts taking orders again.
 *
 * Only meaningful while it is shut: the server answers 409 `shop_is_open`
 * otherwise, which the form treats as good news rather than an error.
 */
export function joinReopenAlerts(phone: string): Promise<{ joined: boolean; message: string }> {
  return api.post<{ joined: boolean; message: string }>(REOPEN_ALERTS_PATH, { phone });
}
